import API_BASE from "./apiConfig";
import React, { useState, useEffect, useCallback } from "react";
import { FaClipboardCheck } from "react-icons/fa";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import {useTheme, getT, getBrandColors, lighten} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";
const BASE = API_BASE;

// ─── Design tokens (non-theme, static) ────────────────────────────────────────
const BRAND = {
  get blue() { return getBrandColors().blue; },
  get yellow() { return getBrandColors().yellow; },
  get green() { return getBrandColors().green; },
  get red() { return getBrandColors().red; },
  get orange() { return getBrandColors().orange; },
  purple: "#a78bfa",
  blur:   "blur(20px)",
  font:   "'Segoe UI', system-ui, sans-serif",
};

const glassCard = (T) => ({
  background:           T.glass,
  backdropFilter:       BRAND.blur, WebkitBackdropFilter: BRAND.blur,
  WebkitBackdropFilter: BRAND.blur,
  border:               T.glassBdr,
  borderRadius:         16,
  boxShadow:            T.glassSh,
});

const DOW_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const STATUS_CONFIG = {
  get Present() { return { color: getBrandColors().green, bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.30)",  label: "Present", short: "P" }; },
  get Absent()  { return { color: getBrandColors().red, bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.30)", label: "Absent",  short: "A" }; },
  get Late()    { return { color: getBrandColors().orange, bg: "rgba(251,191,36,0.12)",  border: "rgba(251,191,36,0.30)",  label: "Late",    short: "L" }; },
  Excused: { color: "#a78bfa", bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.30)", label: "Excused", short: "E" },
};

function today() {
  return new Date().toISOString().split("T")[0];
}

function Badge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Present;
  return (
    <span style={{
      padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`, fontFamily: BRAND.font,
    }}>{cfg.label}</span>
  );
}

function StatusToggle({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {Object.entries(STATUS_CONFIG).map(([s, cfg]) => (
        <button key={s} onClick={() => onChange(s)}
          style={{
            padding: "4px 10px", borderRadius: 8, cursor: "pointer",
            fontSize: 11, fontWeight: 800, transition: "all 0.12s", fontFamily: BRAND.font,
            background: value === s ? cfg.color : cfg.bg,
            color:      value === s ? getBrandColors().blueDark : cfg.color,
            border:     `1px solid ${cfg.border}`,
          }}>
          {cfg.short}
        </button>
      ))}
    </div>
  );
}

// ── Spinner ────────────────────────────────────────────────────────────────────
function Spin() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <span style={{
      display: "inline-block", width: 14, height: 14, borderRadius: "50%",
      border: `2px solid ${T.divider}`, borderTopColor: BRAND.yellow,
      animation: "spin 0.7s linear infinite",
    }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COACH ATTENDANCE
// ─────────────────────────────────────────────────────────────────────────────
export default function CoachAttendance({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  const { config } = useAppConfig();
  const { fmtDate, fmtTime } = useDateTimeFormat();

  const [batches,      setBatches]      = useState([]);
  const [selBatch,     setSelBatch]     = useState("");
  const [selDate,      setSelDate]      = useState(today());
  const [sessionData,  setSessionData]  = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [error,        setError]        = useState("");
  const [activeTab,    setActiveTab]    = useState("attendance"); // attendance | log | report
  const [logData,      setLogData]      = useState([]);
  const [reportData,          setReportData]          = useState(null);
  const [reportFilter,        setReportFilter]        = useState({ level_id: "", batch_id: "", month: "" });
  const [appliedReportFilter, setAppliedReportFilter] = useState(null);
  const [intelData,           setIntelData]           = useState(null);
  const [intelFilter,         setIntelFilter]         = useState({ batch_id: "", month: "" });
  const [intelLoading,        setIntelLoading]        = useState(false);

  // Editable per-student state
  const [attendanceMap, setAttendanceMap] = useState({});
  const [sessionNotes,  setSessionNotes]  = useState("");
  const [incident,      setIncident]      = useState("");

  const orgName = config.org_name || "Sixers Cricket Academy";

  // ── Load batches ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/programs/batches`)
      .then(r => r.json())
      .then(d => { setBatches(d || []); if (d?.length) setSelBatch(String(d[0].batch_id)); })
      .catch(() => {});
  }, []);

  // ── Load session data ───────────────────────────────────────────────────────
  const loadSession = useCallback(async () => {
    if (!selBatch) return;
    setLoading(true); setError(""); setSessionData(null);
    try {
      const r = await fetch(`${BASE}/programs/coach-attendance/session?batch_id=${selBatch}&session_date=${selDate}`);
      const d = await r.json();
      if (!r.ok) {
        setError(`Server error ${r.status}: ${d?.detail || JSON.stringify(d)}`);
        return;
      }
      setSessionData(d);
      const map = {};
      (d.students || []).forEach(s => {
        map[s.student_id] = {
          status:  s.attendance_status || "Present",
          remarks: s.remarks || "",
        };
      });
      setAttendanceMap(map);
      setSessionNotes(d.session_log?.session_notes || "");
      setIncident(d.session_log?.incident_report   || "");
    } catch(err) { setError(`Failed to load session: ${err.message}`); }
    finally { setLoading(false); }
  }, [selBatch, selDate]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // ── Load log history ──────────────────────────────────────────────────────
  const loadLog = useCallback(async () => {
    if (!selBatch) return;
    const r = await fetch(`${BASE}/programs/coach-attendance/log/${selBatch}`);
    const d = await r.json();
    setLogData(d || []);
  }, [selBatch]);

  useEffect(() => { if (activeTab === "log") loadLog(); }, [activeTab, loadLog]);

  // ── Load report ────────────────────────────────────────────────────────────
  const loadReport = useCallback(async () => {
    const p = new URLSearchParams();
    if (reportFilter.level_id) p.set("level_id", reportFilter.level_id);
    if (reportFilter.batch_id) p.set("batch_id", reportFilter.batch_id);
    if (reportFilter.month)    p.set("month",    reportFilter.month);
    const r = await fetch(`${BASE}/programs/reports/overview?${p}`);
    const d = await r.json();
    setReportData(d);
    setAppliedReportFilter({ ...reportFilter });
  }, [reportFilter]);

  // Auto-load only when switching TO the report tab
  useEffect(() => { if (activeTab === "report") loadReport(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load intelligence ────────────────────────────────────────────────────
  const loadIntelligence = useCallback(async () => {
    setIntelLoading(true);
    setIntelData(null);
    const p = new URLSearchParams();
    if (intelFilter.batch_id) p.set("batch_id", intelFilter.batch_id);
    if (intelFilter.month)    p.set("month",    intelFilter.month);
    try {
      const r = await fetch(`${BASE}/programs/reports/coach-effectiveness?${p}`);
      const d = await r.json();
      if (r.ok && d && d.summary && Array.isArray(d.students)) {
        setIntelData(d);
      }
    } catch { /* leave intelData null */ }
    finally { setIntelLoading(false); }
  }, [intelFilter]);

  useEffect(() => { if (activeTab === "intelligence") loadIntelligence(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save attendance ────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!sessionData) return;
    setSaving(true); setSaved(false);
    const attendance = (sessionData.students || []).map(s => ({
      student_id: s.student_id,
      status:     attendanceMap[s.student_id]?.status  || "Present",
      remarks:    attendanceMap[s.student_id]?.remarks || null,
    }));
    try {
      const r = await fetch(`${API_BASE}/programs/coach-attendance/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          batch_id:        parseInt(selBatch),
          session_date:    selDate,
          attendance,
          session_notes:   sessionNotes || null,
          incident_report: incident     || null,
          coach_id:        currentUser?.user_id   || null,
          recorded_by:     currentUser?.full_name || "coach",
        }),
      });
      const d = await r.json();
      if (d.success) { setSaved(true); setTimeout(() => setSaved(false), 3000); loadSession(); }
      else setError(d.detail || "Save failed");
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  };

  // ── Mark all ────────────────────────────────────────────────────────────────
  const markAll = (status) => {
    if (!sessionData) return;
    const map = {};
    sessionData.students.forEach(s => map[s.student_id] = { status, remarks: attendanceMap[s.student_id]?.remarks || "" });
    setAttendanceMap(map);
  };

  const presentCount  = Object.values(attendanceMap).filter(v => v.status === "Present").length;
  const totalCount    = sessionData?.students?.length || 0;
  const selectedBatch = batches.find(b => String(b.batch_id) === String(selBatch));

  const { sorted: sortedSessionStudents, toggleSort: toggleSessionSort, sortArrow: sessionArrow } = useSort(sessionData?.students || [], null);
  const { sorted: sortedLogData, toggleSort: toggleLogSort, sortArrow: logArrow } = useSort(logData, "session_date", "desc");
  const { sorted: sortedReportStudents, toggleSort: toggleReportSort, sortArrow: reportArrow } = useSort(reportData?.students || [], null);
  const { sorted: sortedIntelStudents, toggleSort: toggleIntelSort, sortArrow: intelArrow } = useSort(intelData?.students || [], null);

  // ── Shared input style ─────────────────────────────────────────────────────
  const inputStyle = {
    background: T.inputBg,
    border: `1.5px solid ${T.inputBdr}`,
    color: T.inputText,
    borderRadius: 10,
    padding: "9px 13px",
    fontSize: 14,
    fontFamily: BRAND.font,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    colorScheme: T.colorScheme,
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: "100dvh",
      background: T.pageBg,
      padding: "20px 28px 48px",
      fontFamily: T.font,
    }}>

      {/* ── Page Header ── */}
      <div style={{
        ...gc,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 24px", marginBottom: 22,
        background: isDark ? `linear-gradient(118deg, ${getBrandColors().blueDark} 0%, ${getBrandColors().blue} 60%, ${lighten(getBrandColors().blue,45)} 100%)` : undefined,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {/* Golden icon badge — always visible, prominent in light mode */}
          <PageHeaderIcon icon={<FaClipboardCheck size={20} color={getBrandColors().blueDark} />} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: isDark ? `${BRAND.yellow}cc` : BRAND.blue, textTransform: "uppercase", letterSpacing: "0.12em" }}>{orgName}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: T.textPrimary, lineHeight: 1.2 }}>Coach Attendance</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 1 }}>Mark and manage session attendance</div>
          </div>
        </div>
        {/* Stats pills */}
        {sessionData && (
          <div style={{ display: "flex", gap: 12 }}>
            {[
              { label: "Total",   val: totalCount,   color: BRAND.yellow },
              { label: "Present", val: presentCount, color: BRAND.green  },
              { label: "Absent",  val: Object.values(attendanceMap).filter(v => v.status === "Absent").length, color: BRAND.red },
            ].map(pill => (
              <div key={pill.label} style={{
                textAlign: "center", padding: "8px 18px", borderRadius: 10,
                background: T.divider, border: `1px solid ${T.inputBdr}`,
              }}>
                <div style={{ fontSize: 24, fontWeight: 900, color: pill.color, lineHeight: 1 }}>{pill.val}</div>
                <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{pill.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[
          ["attendance", "📋 Attendance"],
          ["log",        "📅 Session Log"],
          ["report",     "📊 Report"],
          ["intelligence","📈 Intelligence"],
        ].map(([id, lbl]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            padding: "9px 20px", borderRadius: 10, cursor: "pointer",
            fontSize: 13, fontWeight: 700, fontFamily: BRAND.font, transition: "all 0.15s",
            background: activeTab === id
              ? `linear-gradient(135deg,${BRAND.yellow},#e8b800)`
              : T.glass,
            color:      activeTab === id ? getBrandColors().blueDark : T.textLabel,
            border:     activeTab === id ? "none" : `1px solid ${T.inputBdr}`,
            boxShadow:  activeTab === id ? "0 4px 14px rgba(247,206,11,0.35)" : "none",
            fontWeight: activeTab === id ? 800 : 700,
          }}>{lbl}</button>
        ))}
      </div>

      {/* ── TAB: ATTENDANCE ── */}
      {activeTab === "attendance" && (
        <>
          {/* Controls bar */}
          <div style={{ ...gc, padding: "16px 20px", marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            {/* Batch select */}
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textLabel, display: "block", marginBottom: 4 }}>Batch</label>
              <select value={selBatch} onChange={e => setSelBatch(e.target.value)} style={{ ...inputStyle , colorScheme: T.colorScheme }}>
                {batches.map(b => (
                  <option key={b.batch_id} value={b.batch_id}>{b.batch_name} ({b.level_name})</option>
                ))}
              </select>
            </div>
            {/* Date */}
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textLabel, display: "block", marginBottom: 4 }}>Date</label>
              <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)} style={{ ...inputStyle }} />
            </div>
            {/* Batch info pill */}
            {selectedBatch && (
              <div style={{
                padding: "8px 16px", borderRadius: 10,
                background: "rgba(3,78,123,0.20)", border: "1px solid rgba(3,78,123,0.35)",
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: BRAND.yellow }}>{selectedBatch.batch_name}</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>{fmtTime(selectedBatch.start_time)} – {fmtTime(selectedBatch.end_time)}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>
                  {(selectedBatch.schedule_days || []).map(d => DOW_LABELS[d]).join(", ")}
                </div>
              </div>
            )}
            {/* Mark all buttons */}
            <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, alignSelf: "center" }}>Mark All:</span>
              {["Present", "Absent", "Late"].map(s => (
                <button key={s} onClick={() => markAll(s)} style={{
                  padding: "6px 14px", borderRadius: 8, cursor: "pointer",
                  fontSize: 12, fontWeight: 800, fontFamily: BRAND.font,
                  background: STATUS_CONFIG[s].bg,
                  color:      STATUS_CONFIG[s].color,
                  border:     `1px solid ${STATUS_CONFIG[s].border}`,
                }}>
                  {STATUS_CONFIG[s].short} {s}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div style={{
              padding: "12px 18px", borderRadius: 10, marginBottom: 16,
              background: "rgba(248,113,113,0.12)", color: T.red, fontWeight: 600,
              border: "1px solid rgba(248,113,113,0.30)",
            }}>{error}</div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: T.textMuted }}><Spin /> Loading session…</div>
          ) : sessionData && sessionData.students?.length === 0 ? (
            <div style={{ ...gc, padding: "48px", textAlign: "center", color: T.textMuted }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.inputText }}>No students enrolled in this batch</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Use Student Enrollment to add students to this batch.</div>
            </div>
          ) : sessionData && (
            <>
              {/* Student attendance table */}
              <div style={{ ...gc, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font }}>
                    <thead>
                      <tr style={{ background: T.rowOdd, borderBottom: T.rowBdr }}>
                        {[
                          { h: "#", field: null },
                          { h: "Student", field: "student_name" },
                          { h: "Program", field: "program_name" },
                          { h: "Level", field: "level_name" },
                          { h: "Status", field: null },
                          { h: "Remarks", field: null },
                        ].map(c => (
                          <th key={c.h} onClick={() => c.field && toggleSessionSort(c.field)} style={{
                            padding: "12px 14px", textAlign: "left",
                            fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                            letterSpacing: "0.08em", color: T.textMuted, whiteSpace: "nowrap",
                            cursor: c.field ? "pointer" : "default", userSelect: "none",
                          }}>{c.h}{c.field && sessionArrow(c.field)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSessionStudents.map((s, i) => {
                        const att = attendanceMap[s.student_id] || { status: "Present", remarks: "" };
                        return (
                          <tr key={s.student_id}
                            style={{
                              borderBottom: T.rowBdr,
                              background: i % 2 === 0 ? T.rowEven : T.rowOdd,
                              transition: "background 0.12s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = T.glass}
                            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.rowEven : T.rowOdd}>
                            <td style={{ padding: "10px 14px", fontSize: 12, color: T.textMuted }}>{i + 1}</td>
                            <td style={{ padding: "10px 14px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{
                                  width: 32, height: 32, borderRadius: "50%",
                                  background: `linear-gradient(135deg,${BRAND.yellow},#e8b800)`,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 12, fontWeight: 800, color: getBrandColors().blueDark,
                                  flexShrink: 0, overflow: "hidden",
                                }}>
                                  {s.photo_url
                                    ? <img src={`${BASE}/${s.photo_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    : s.student_name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                                </div>
                                <div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: T.inputText }}>{s.student_name}</div>
                                  <div style={{ fontSize: 11, color: T.textMuted }}>ID: {s.student_id}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: T.textSub }}>{s.program_name}</td>
                            <td style={{ padding: "10px 14px" }}>
                              <span style={{
                                padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800,
                                background: "rgba(3,78,123,0.30)", color: T.yellow,
                                border: "1px solid rgba(3,78,123,0.50)",
                              }}>{s.level_name}</span>
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              <StatusToggle value={att.status} onChange={val => setAttendanceMap(m => ({ ...m, [s.student_id]: { ...att, status: val } }))} />
                            </td>
                            <td style={{ padding: "10px 14px" }}>
                              <input
                                value={att.remarks}
                                onChange={e => setAttendanceMap(m => ({ ...m, [s.student_id]: { ...att, remarks: e.target.value } }))}
                                placeholder="Optional note…"
                                style={{
                                  padding: "6px 10px", borderRadius: 7,
                                  border: `1.5px solid ${T.inputBdr}`,
                                  background: T.inputBg,
                                  fontSize: 12, fontFamily: BRAND.font, outline: "none",
                                  width: 160, color: T.inputText,
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Session notes + incident */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div style={{ ...gc, padding: "20px" }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textLabel, display: "block", marginBottom: 8 }}>📝 Session Notes</label>
                  <textarea
                    value={sessionNotes}
                    onChange={e => setSessionNotes(e.target.value)}
                    rows={4}
                    placeholder="Overall session notes, drills covered, highlights…"
                    style={{
                      width: "100%", padding: "10px", borderRadius: 9,
                      border: `1.5px solid ${T.inputBdr}`,
                      background: T.inputBg,
                      fontSize: 13, fontFamily: BRAND.font, resize: "vertical", outline: "none",
                      boxSizing: "border-box", color: T.inputText,
                    }}
                  />
                </div>
                <div style={{ ...gc, padding: "20px" }}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(248,113,113,0.80)", display: "block", marginBottom: 8 }}>⚠️ Incident Report</label>
                  <textarea
                    value={incident}
                    onChange={e => setIncident(e.target.value)}
                    rows={4}
                    placeholder="Any incidents, injuries or behavioural issues…"
                    style={{
                      width: "100%", padding: "10px", borderRadius: 9,
                      border: "1.5px solid rgba(248,113,113,0.30)",
                      background: "rgba(248,113,113,0.06)",
                      fontSize: 13, fontFamily: BRAND.font, resize: "vertical", outline: "none",
                      boxSizing: "border-box", color: T.inputText,
                    }}
                  />
                </div>
              </div>

              {/* Save bar */}
              <div style={{ ...gc, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, color: T.textMuted }}>
                  {presentCount} of {totalCount} students marked present
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {saved && <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.green }}>✓ Saved successfully</div>}
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      padding: "11px 32px", borderRadius: 10, cursor: saving ? "not-allowed" : "pointer",
                      background: saving ? T.divider : `linear-gradient(135deg,${BRAND.yellow},#e8b800)`,
                      color: saving ? T.textMuted : getBrandColors().blueDark,
                      fontSize: 14, fontWeight: 800, fontFamily: BRAND.font,
                      border: "none",
                      boxShadow: saving ? "none" : "0 4px 16px rgba(247,206,11,0.30)",
                      display: "flex", gap: 8, alignItems: "center",
                    }}>
                    {saving ? <><Spin /> Saving…</> : "💾 Save Attendance"}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── TAB: SESSION LOG ── */}
      {activeTab === "log" && (
        <div style={{ ...gc, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: T.rowBdr }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.inputText }}>Session History</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
              Batch: {selectedBatch?.batch_name || "—"} · Last 30 sessions
            </div>
          </div>
          {logData.length === 0 ? (
            <div style={{ padding: "48px", textAlign: "center", color: T.textMuted }}>No sessions logged yet.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: BRAND.font }}>
                <thead>
                  <tr style={{ background: T.rowOdd, borderBottom: T.rowBdr }}>
                    {[
                      { h: "Date", field: "session_date" },
                      { h: "Present", field: "students_present" },
                      { h: "Coach", field: "coach_name" },
                      { h: "Notes", field: "session_notes" },
                      { h: "Incident", field: "incident_report" },
                    ].map(c => (
                      <th key={c.h} onClick={() => toggleLogSort(c.field)} style={{
                        padding: "12px 14px", textAlign: "left",
                        fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                        letterSpacing: "0.08em", color: T.textMuted,
                        cursor: "pointer", userSelect: "none",
                      }}>{c.h}{logArrow(c.field)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedLogData.map((row, i) => (
                    <tr key={row.log_id} style={{
                      borderBottom: T.rowBdr,
                      background: i % 2 === 0 ? T.rowEven : T.rowOdd,
                    }}>
                      <td style={{ padding: "10px 14px", fontSize: 14, fontWeight: 700, color: T.inputText }}>{fmtDate(row.session_date)}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{
                          padding: "3px 12px", borderRadius: 20,
                          background: "rgba(52,211,153,0.12)", color: BRAND.green,
                          fontWeight: 800, fontSize: 13, border: "1px solid rgba(52,211,153,0.30)",
                        }}>{row.students_present}</span>
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: T.textMuted }}>{row.coach_name || "—"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 12, color: T.textMuted, maxWidth: 240 }}>
                        {row.session_notes
                          ? <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", maxWidth: 220 }}>{row.session_notes}</span>
                          : "—"}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        {row.incident_report
                          ? <span style={{ padding: "2px 10px", borderRadius: 20, background: "rgba(248,113,113,0.12)", color: BRAND.red, fontSize: 11, fontWeight: 700, border: "1px solid rgba(248,113,113,0.30)" }}>⚠ Yes</span>
                          : <span style={{ color: T.textMuted, fontSize: 12 }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: REPORT ── */}
      {activeTab === "report" && (
        <>
          {/* Filters */}
          <div style={{ ...gc, padding: "16px 20px", marginBottom: 16, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textLabel, display: "block", marginBottom: 4 }}>Batch</label>
              <select
                value={reportFilter.batch_id}
                onChange={e => setReportFilter(f => ({ ...f, batch_id: e.target.value }))}
                style={{ ...inputStyle, minWidth: 180, width: "auto" , colorScheme: T.colorScheme }}>
                <option value="">All Batches</option>
                {batches.map(b => <option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textLabel, display: "block", marginBottom: 4 }}>Month</label>
              <input
                type="month"
                value={reportFilter.month}
                onChange={e => setReportFilter(f => ({ ...f, month: e.target.value }))}
                style={{ ...inputStyle, width: "auto" }}
              />
            </div>
            <button
              onClick={loadReport}
              style={{
                padding: "10px 24px", borderRadius: 10, cursor: "pointer", border: "none",
                background: `linear-gradient(135deg,${BRAND.yellow},#e8b800)`,
                color: getBrandColors().blueDark, fontWeight: 800, fontSize: 13, fontFamily: BRAND.font,
                boxShadow: "0 4px 14px rgba(247,206,11,0.28)",
              }}>
              🔍 Run Report
            </button>
          </div>

          {/* Report table */}
          {reportData && (
            <div style={{ ...gc, overflow: "hidden" }}>
              <div style={{ padding: "14px 20px", borderBottom: T.rowBdr }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.inputText }}>Student Overview Report</div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{reportData.total} students</div>
                </div>
                {/* Applied filter summary */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                  {appliedReportFilter?.batch_id
                    ? <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(3,78,123,0.25)", color: BRAND.yellow, border: "1px solid rgba(3,78,123,0.40)" }}>
                        Batch: {batches.find(b => String(b.batch_id) === String(appliedReportFilter.batch_id))?.batch_name || appliedReportFilter.batch_id}
                      </span>
                    : <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(3,78,123,0.25)", color: BRAND.yellow, border: "1px solid rgba(3,78,123,0.40)" }}>All Batches</span>
                  }
                  {appliedReportFilter?.month && (
                    <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(167,139,250,0.12)", color: BRAND.purple, border: "1px solid rgba(167,139,250,0.30)" }}>
                      Month: {appliedReportFilter.month}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: BRAND.font }}>
                  <thead>
                    <tr style={{ background: T.rowOdd, borderBottom: T.rowBdr }}>
                      {[
                        { h: "Student", field: "student_name" },
                        { h: "Level", field: "level_name" },
                        { h: "Program", field: "program_name" },
                        { h: "Batch", field: "batch_name" },
                        { h: "Schedule", field: "start_time" },
                        { h: "Present (Month)", field: "present_count" },
                        { h: "Avg Score", field: "avg_score" },
                        { h: "Enrolled", field: "enroll_date" },
                      ].map(c => (
                        <th key={c.h} onClick={() => toggleReportSort(c.field)} style={{
                          padding: "11px 14px", textAlign: "left",
                          fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                          letterSpacing: "0.07em", color: T.textMuted, whiteSpace: "nowrap",
                          cursor: "pointer", userSelect: "none",
                        }}>{c.h}{reportArrow(c.field)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedReportStudents.map((s, i) => (
                      <tr key={s.student_id} style={{
                        borderBottom: T.rowBdr,
                        background: i % 2 === 0 ? T.rowEven : T.rowOdd,
                      }}>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: T.inputText }}>{s.student_name}</div>
                          <div style={{ fontSize: 11, color: T.textMuted }}>ID: {s.student_id}</div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800, background: "rgba(3,78,123,0.25)", color: BRAND.yellow, border: "1px solid rgba(3,78,123,0.40)" }}>{s.level_name}</span>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 13, color: T.textSub, whiteSpace: "nowrap" }}>{s.program_name}</td>
                        <td style={{ padding: "10px 14px", fontSize: 13, color: T.textSub, fontWeight: 600 }}>{s.batch_name}</td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: T.textMuted, whiteSpace: "nowrap" }}>{fmtTime(s.start_time)} – {fmtTime(s.end_time)}</td>
                        <td style={{ padding: "10px 14px", textAlign: "center" }}>
                          <span style={{ fontSize: 15, fontWeight: 900, color: s.present_count >= s.sessions_per_month * 0.8 ? BRAND.green : BRAND.orange }}>{s.present_count}</span>
                          <span style={{ fontSize: 11, color: T.textMuted }}>/{s.sessions_per_month}</span>
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "center" }}>
                          <span style={{ fontSize: 14, fontWeight: 800, color: s.avg_score >= 7 ? BRAND.green : s.avg_score >= 5 ? BRAND.orange : BRAND.red }}>{s.avg_score || "—"}</span>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: T.textMuted }}>{s.enroll_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── TAB: INTELLIGENCE ── */}
      {activeTab === "intelligence" && (
        <>
          {/* Filter bar */}
          <div style={{ ...gc, padding: "16px 20px", marginBottom: 18, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textLabel, display: "block", marginBottom: 4 }}>Batch</label>
              <select
                value={intelFilter.batch_id}
                onChange={e => setIntelFilter(f => ({ ...f, batch_id: e.target.value }))}
                style={{ ...inputStyle , colorScheme: T.colorScheme }}>
                <option value="">All Batches</option>
                {batches.map(b => (
                  <option key={b.batch_id} value={b.batch_id}>{b.batch_name}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textLabel, display: "block", marginBottom: 4 }}>Month</label>
              <input
                type="month"
                value={intelFilter.month}
                onChange={e => setIntelFilter(f => ({ ...f, month: e.target.value }))}
                style={{ ...inputStyle }}
              />
            </div>
            <button
              onClick={loadIntelligence}
              disabled={intelLoading}
              style={{
                padding: "10px 26px", borderRadius: 10, cursor: "pointer", border: "none",
                fontSize: 14, fontWeight: 800, fontFamily: BRAND.font,
                background: `linear-gradient(135deg,${BRAND.yellow},#e8b800)`,
                get color() { return getBrandColors().blueDark; },
                boxShadow: intelLoading ? "none" : "0 4px 14px rgba(247,206,11,0.28)",
                opacity: intelLoading ? 0.7 : 1,
              }}>
              {intelLoading ? <><Spin /> Analyzing…</> : "🔍 Analyze"}
            </button>
          </div>

          {intelLoading && (
            <div style={{ textAlign: "center", padding: 48, color: T.textMuted, fontSize: 15 }}><Spin /> Loading intelligence…</div>
          )}

          {!intelLoading && intelData?.summary && (
            <>
              {/* ── Summary Scorecard ── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 20 }}>
                {[
                  {
                    icon: "🚨", label: "Action Required",
                    val: intelData.summary.high_alerts,
                    accent: "#60a5fa",
                  },
                  {
                    icon: "✅", label: "Compliance Rate",
                    val: `${intelData.summary.avg_compliance_pct}%`,
                    accent: "#60a5fa",
                  },
                  {
                    icon: "⚠️", label: "Students at Risk",
                    val: intelData.summary.students_low_attendance,
                    accent: "#60a5fa",
                  },
                  {
                    icon: "📈", label: "Improving",
                    val: intelData.summary.students_improving,
                    accent: "#60a5fa",
                  },
                ].map(card => (
                  <div key={card.label} style={{
                    ...gc,
                    padding: "12px 16px",
                    display: "flex", alignItems: "center", gap: 14,
                    position: "relative", overflow: "hidden",
                  }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                      background: card.accent + "28",
                      border: `1px solid ${card.accent}33`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: `0 2px 10px ${card.accent}18`,
                      fontSize: 24,
                    }}>{card.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: T.textMuted, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{card.label}</div>
                      <div style={{ fontSize: 28, lineHeight: 1.05, fontWeight: 900, letterSpacing: "-0.03em", color: T.textPrimary, fontFamily: "'SF Pro Display','Segoe UI',system-ui,sans-serif" }}>{card.val}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Action Items ── */}
              {intelData.alerts.length > 0 && (
                <div style={{ ...gc, padding: "18px 22px", marginBottom: 20 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: T.inputText, marginBottom: 14 }}>🚦 Action Items</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {intelData.alerts.map((alert, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                        borderRadius: 10,
                        border: `1px solid ${alert.severity === "high" ? "rgba(248,113,113,0.30)" : "rgba(251,191,36,0.30)"}`,
                        background: alert.severity === "high" ? "rgba(248,113,113,0.10)" : "rgba(251,191,36,0.08)",
                      }}>
                        <span style={{
                          padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 800, whiteSpace: "nowrap",
                          background: alert.severity === "high" ? BRAND.red : BRAND.orange,
                          color: getBrandColors().blueDark, textTransform: "uppercase", letterSpacing: "0.07em",
                        }}>{alert.severity === "high" ? "HIGH" : "MED"}</span>
                        <span style={{ flex: 1, fontSize: 13, color: T.textSub, fontWeight: 500 }}>{alert.message}</span>
                        {alert.type === "missed_marking" && alert.date && (
                          <button onClick={() => {
                            setSelBatch(String(alert.batch_id));
                            setSelDate(alert.date);
                            setActiveTab("attendance");
                          }} style={{
                            padding: "5px 14px", borderRadius: 8,
                            border: "1px solid rgba(247,206,11,0.40)", cursor: "pointer",
                            fontSize: 12, fontWeight: 700,
                            background: "rgba(247,206,11,0.10)", color: BRAND.yellow,
                            whiteSpace: "nowrap", fontFamily: BRAND.font,
                          }}>→ Mark Now</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {intelData.alerts.length === 0 && (
                <div style={{
                  ...gc, padding: "22px", marginBottom: 20, textAlign: "center",
                  background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.22)",
                }}>
                  <div style={{ fontSize: 32, marginBottom: 6 }}>🎉</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: BRAND.green }}>All clear! No action items for this period.</div>
                </div>
              )}

              {/* ── Compliance Calendar per Batch ── */}
              <div style={{ ...gc, padding: "20px 22px", marginBottom: 20 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: T.inputText, marginBottom: 16 }}>📅 Attendance Compliance Calendar</div>
                {intelData.compliance.map(comp => {
                  const allExp    = comp.all_expected || [];
                  const loggedSet = new Set(comp.logged_dates  || []);
                  const missSet   = new Set(comp.missing_dates || []);
                  return (
                    <div key={comp.batch_id} style={{ marginBottom: 18 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: T.inputText }}>{comp.batch_name}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: comp.compliance_pct >= 90 ? BRAND.green : comp.compliance_pct >= 70 ? BRAND.orange : BRAND.red }}>
                          {comp.compliance_pct}% ({comp.logged_days}/{comp.expected_days} sessions)
                        </span>
                      </div>
                      {allExp.length === 0 ? (
                        <div style={{ fontSize: 12, color: T.textMuted }}>No scheduled sessions this period.</div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                          {allExp.map(d => {
                            const isLogged = loggedSet.has(d);
                            const isMissed = missSet.has(d);
                            const dayLabel = d.slice(8);
                            const dayName  = new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" });
                            return (
                              <div key={d}
                                title={`${d} (${dayName}) — ${isLogged ? "Logged" : "Missed"}`}
                                onClick={() => {
                                  if (isMissed) {
                                    setSelBatch(String(comp.batch_id));
                                    setSelDate(d);
                                    setActiveTab("attendance");
                                  }
                                }}
                                style={{
                                  width: 40, height: 44, borderRadius: 8,
                                  display: "flex", flexDirection: "column",
                                  alignItems: "center", justifyContent: "center",
                                  cursor: isMissed ? "pointer" : "default",
                                  background: isLogged
                                    ? "rgba(52,211,153,0.25)"
                                    : isMissed
                                    ? "rgba(248,113,113,0.25)"
                                    : T.glass,
                                  border: isLogged
                                    ? "1px solid rgba(52,211,153,0.45)"
                                    : isMissed
                                    ? "1px solid rgba(248,113,113,0.45)"
                                    : `1px solid ${T.inputBdr}`,
                                  color: isLogged ? BRAND.green : isMissed ? BRAND.red : T.textMuted,
                                  transition: "transform 0.1s",
                                }}>
                                <span style={{ fontSize: 9, fontWeight: 700, opacity: 0.85 }}>{dayName}</span>
                                <span style={{ fontSize: 14, fontWeight: 900, lineHeight: 1 }}>{dayLabel}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Student Trend Table ── */}
              {intelData.students.length > 0 && (
                <div style={{ ...gc, padding: "20px 22px" }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: T.inputText, marginBottom: 14 }}>👥 Student Performance Trends</div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: BRAND.font }}>
                      <thead>
                        <tr style={{ background: T.rowOdd, borderBottom: T.rowBdr }}>
                          {[
                            { h: "Student", field: "student_name" },
                            { h: "Batch", field: "batch_name" },
                            { h: "Attendance", field: "attendance_pct" },
                            { h: "Sessions", field: "present_this_month" },
                            { h: "Score (Month)", field: "score_this_month" },
                            { h: "Score (Prev)", field: "score_prev_month" },
                            { h: "Trend", field: "trend_direction" },
                            { h: "Status", field: null },
                          ].map(c => (
                            <th key={c.h} onClick={() => c.field && toggleIntelSort(c.field)} style={{
                              padding: "10px 12px", textAlign: "left",
                              fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                              letterSpacing: "0.07em", color: T.textMuted, whiteSpace: "nowrap",
                              cursor: c.field ? "pointer" : "default", userSelect: "none",
                            }}>{c.h}{c.field && intelArrow(c.field)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedIntelStudents.map((s, i) => {
                          const attColor  = s.attendance_pct == null ? T.textMuted : s.attendance_pct >= 80 ? BRAND.green : s.attendance_pct >= 60 ? BRAND.orange : BRAND.red;
                          const trendIcon = s.trend_direction === "up" ? "▲" : s.trend_direction === "down" ? "▼" : s.trend_direction === "stable" ? "→" : "—";
                          const trendColor = s.trend_direction === "up" ? BRAND.green : s.trend_direction === "down" ? BRAND.red : T.textMuted;
                          const statuses  = [];
                          if (s.attendance_pct != null && s.attendance_pct < 80) statuses.push({ label: "Low Att",  color: BRAND.orange, bg: "rgba(251,191,36,0.12)",  border: "rgba(251,191,36,0.30)" });
                          if (s.trend_direction === "down") statuses.push({ label: "Declining", color: BRAND.red,    bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.30)" });
                          if (s.trend_direction === "up")   statuses.push({ label: "Improving", color: BRAND.green,  bg: "rgba(52,211,153,0.12)",  border: "rgba(52,211,153,0.30)" });
                          return (
                            <tr key={s.student_id} style={{
                              borderBottom: T.rowBdr,
                              background: i % 2 === 0 ? T.rowEven : T.rowOdd,
                            }}>
                              <td style={{ padding: "10px 12px" }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.inputText }}>{s.student_name}</div>
                              </td>
                              <td style={{ padding: "10px 12px", fontSize: 12, color: T.textMuted }}>{s.batch_name}</td>
                              <td style={{ padding: "10px 12px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ width: 60, height: 6, borderRadius: 3, background: T.divider, overflow: "hidden" }}>
                                    <div style={{ width: `${Math.min(s.attendance_pct || 0, 100)}%`, height: "100%", background: attColor, borderRadius: 3, transition: "width 0.4s" }} />
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 800, color: attColor }}>
                                    {s.attendance_pct != null ? `${s.attendance_pct}%` : "—"}
                                  </span>
                                </div>
                              </td>
                              <td style={{ padding: "10px 12px", fontSize: 12, color: T.textMuted, textAlign: "center" }}>
                                {s.present_this_month}/{s.sessions_expected}
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "center" }}>
                                <span style={{ fontSize: 14, fontWeight: 900, color: s.score_this_month != null ? (s.score_this_month >= 7 ? BRAND.green : s.score_this_month >= 5 ? BRAND.orange : BRAND.red) : T.textMuted }}>
                                  {s.score_this_month != null ? s.score_this_month : "—"}
                                </span>
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "center", fontSize: 13, color: T.textMuted }}>
                                {s.score_prev_month != null ? s.score_prev_month : "—"}
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "center" }}>
                                <span style={{ fontSize: 18, fontWeight: 900, color: trendColor }}>{trendIcon}</span>
                              </td>
                              <td style={{ padding: "10px 12px" }}>
                                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                                  {statuses.length === 0
                                    ? <span style={{ fontSize: 11, color: BRAND.green, fontWeight: 700 }}>On Track</span>
                                    : statuses.map(st => (
                                        <span key={st.label} style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 800, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>{st.label}</span>
                                      ))
                                  }
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {!intelLoading && !intelData?.summary && (
            <div style={{ textAlign: "center", padding: 48, color: T.textMuted }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📈</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.textLabel }}>Select filters and click Analyze to see the intelligence report.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
